import { useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { TaskCalendar } from '@/components/tasks/TaskCalendar';
import { TaskList } from '@/components/tasks/TaskList';
import { TaskListView } from '@/components/tasks/TaskListView';
import { NoDueDateTasksCard } from '@/components/tasks/NoDueDateTasksCard';
import { TaskFilters, type TaskView } from '@/components/tasks/TaskFilters';
import { CreateTaskDialog } from '@/components/tasks/CreateTaskDialog';
import { UpdateTaskModal } from '@/components/claims/detail/modals/UpdateTaskModal';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAllTasks } from '@/hooks/use-all-tasks';
import {
  applyTaskFilters,
  getUniqueTaskAssignees,
  type TaskAssigneeFilter,
  type TaskPriorityFilter,
} from '@/lib/task-filters';
import { AlertCircle } from 'lucide-react';
import type { ClaimTask } from '@/api/generated/client';

export default function Tasks() {
  const {
    tasks,
    claimsMap,
    isLoading,
    isError,
    error,
    refetch,
    selectedDate,
    setSelectedDate,
    handleCreateTask,
    handleCompleteTask,
  } = useAllTasks();

  const [taskBeingEdited, setTaskBeingEdited] = useState<ClaimTask | null>(null);
  const [view, setView] = useState<TaskView>('list');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriorityFilter>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<TaskAssigneeFilter>('all');

  // Only users actually assigned to at least one task — so every option in the
  // dropdown will actually match at least one task when selected.
  const assigneeOptions = useMemo(() => getUniqueTaskAssignees(tasks), [tasks]);

  const filteredTasks = useMemo(
    () => applyTaskFilters(tasks, { priorityFilter, assigneeFilter }),
    [tasks, priorityFilter, assigneeFilter],
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6 md:px-4 md:py-8">
          <PageHeader
            title="Tasks"
            description="Manage and track tasks across all claims"
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Skeleton className="h-[600px] w-full" />
            </div>
            <div>
              <Skeleton className="h-[600px] w-full" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className="md:px-4 md:py-8">
          <PageHeader
            title="Tasks"
            description="Manage and track tasks across all claims"
          />
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load tasks</AlertTitle>
            <AlertDescription className="flex items-center gap-4">
              <span>{error instanceof Error ? error.message : 'An unexpected error occurred.'}</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 md:px-4 md:py-8">
        <PageHeader
          title="Tasks"
          description="Manage and track tasks across all claims"
        >
          <AppErrorBoundary>
            <CreateTaskDialog onCreateTask={handleCreateTask} />
          </AppErrorBoundary>
        </PageHeader>

        <TaskFilters
          view={view}
          onViewChange={setView}
          priorityFilter={priorityFilter}
          onPriorityFilterChange={setPriorityFilter}
          assigneeFilter={assigneeFilter}
          onAssigneeFilterChange={setAssigneeFilter}
          assigneeOptions={assigneeOptions}
        />

        {view === 'calendar' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar - left column, first row on large screens; first when stacked. */}
            <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1">
              <AppErrorBoundary>
                <TaskCalendar
                  tasks={filteredTasks}
                  onDateSelect={setSelectedDate}
                  selectedDate={selectedDate}
                />
              </AppErrorBoundary>
            </div>

            {/* Upcoming Tasks List - right column on large screens, spanning both rows so its height
                tracks the left column (calendar + no due date card). At lg the inner wrapper is
                absolutely positioned so TaskList's own content does NOT drive the grid row height; the
                row height is set by the left column and the wrapper stretches to fill it, so a day with
                many tasks scrolls inside TaskList's ScrollArea instead of growing the page. Below lg it
                stacks normally, appearing directly under the calendar. */}
            <div className="relative min-h-0 lg:col-start-3 lg:row-start-1 lg:row-span-2">
              <div className="lg:absolute lg:inset-0">
                <AppErrorBoundary>
                  <TaskList
                    tasks={filteredTasks}
                    claimsMap={claimsMap}
                    selectedDate={selectedDate}
                    onTaskComplete={handleCompleteTask}
                    onTaskEdit={setTaskBeingEdited}
                  />
                </AppErrorBoundary>
              </div>
            </div>

            {/* No due date card - left column, second row on large screens (below the calendar). When
                stacked (below lg) it appears last, below the "Tasks for …" list. */}
            <div className="lg:col-span-2 lg:col-start-1 lg:row-start-2">
              <AppErrorBoundary>
                <NoDueDateTasksCard
                  tasks={filteredTasks}
                  claimsMap={claimsMap}
                  onTaskComplete={handleCompleteTask}
                  onTaskEdit={setTaskBeingEdited}
                />
              </AppErrorBoundary>
            </div>
          </div>
        ) : (
          <AppErrorBoundary>
            <TaskListView
              tasks={filteredTasks}
              claimsMap={claimsMap}
              onTaskComplete={handleCompleteTask}
              onTaskEdit={setTaskBeingEdited}
            />
          </AppErrorBoundary>
        )}
        <UpdateTaskModal
          claimId={taskBeingEdited?.claimId ?? 0}
          task={taskBeingEdited}
          open={taskBeingEdited !== null}
          onOpenChange={(open) => {
            if (!open) setTaskBeingEdited(null);
          }}
        />
      </div>
    </Layout>
  );
}
